import { useEffect, useId, useMemo, useRef, useState, type RefObject } from 'react';
import {
  AlertTriangle,
  Banknote,
  CheckCircle2,
  Copy,
  Database,
  Download,
  Eye,
  EyeOff,
  FileText,
  Filter,
  Home,
  Landmark,
  Pencil,
  Plus,
  Receipt,
  RefreshCw,
  Save,
  Search,
  Share2,
  Settings as SettingsIcon,
  Trash2,
  Upload,
  Wallet,
  X
} from 'lucide-react';
import { calculateCurrentBalances, calculateMonthlySummary, getKnownMonths, getMonthRange, suggestReportNotes } from './domain/calculations';
import { defaultState, freshState } from './domain/defaults';
import {
  accountLabels,
  currentMonthKey,
  formatMoney,
  formatMonth,
  paymentMethodLabels,
  safeNumber,
  shortAccountLabels,
  todayInputValue,
  typeLabels
} from './domain/format';
import type {
  AccountKey,
  EntryDraft,
  MonthlyReportRow,
  PaymentMethod,
  ReportNotes,
  StoredTreasuryState,
  TransactionType,
  TreasurySettings,
  TreasuryTransaction
} from './domain/types';
import { LocalStorageTreasuryRepository, normalizeState } from './storage/repository';
import { backupToJson, downloadBlob, downloadText, monthlyReportToCsv, monthlyReportsToCsv, transactionsToCsv } from './utils/export';
import { createMonthlyReportPdfBlob } from './utils/reportPdf';

type ViewKey = 'dashboard' | 'entries' | 'report' | 'settings';
type EntryFilter = 'all' | TransactionType;
type AccountFilter = 'all' | AccountKey;
type SaveMode = 'close' | 'again';

const repository = new LocalStorageTreasuryRepository();
const REPORT_PREVIEW_WIDTH = 1080;

const navItems: Array<{ key: ViewKey; label: string; icon: typeof Home }> = [
  { key: 'dashboard', label: 'Dashboard', icon: Home },
  { key: 'entries', label: 'Entries', icon: Receipt },
  { key: 'report', label: 'Monthly report', icon: FileText },
  { key: 'settings', label: 'Settings', icon: SettingsIcon }
];

const paymentMethods: PaymentMethod[] = ['cash', 'bank-transfer', 'mobile-money', 'cheque', 'other'];
const monthOptions = Array.from({ length: 12 }, (_, index) => ({
  value: index + 1,
  label: new Intl.DateTimeFormat('en-GH', { month: 'short' }).format(new Date(2026, index, 1))
}));

function App() {
  const [state, setState] = useState<StoredTreasuryState>(() => repository.load());
  const [view, setView] = useState<ViewKey>('dashboard');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const loaded = repository.load();
    return getPreferredMonth(loaded.transactions, loaded.settings.firstReportMonth);
  });
  const [drawer, setDrawer] = useState<{ open: boolean; draft: EntryDraft }>({
    open: false,
    draft: makeDraft('income', defaultState.settings, currentMonthKey(), [])
  });

  const months = useMemo(
    () => getKnownMonths(state.transactions, currentMonthKey(), state.settings.firstReportMonth),
    [state.settings.firstReportMonth, state.transactions]
  );
  const summary = useMemo(() => calculateMonthlySummary(state, selectedMonth), [state, selectedMonth]);
  const balances = useMemo(() => calculateCurrentBalances(state, selectedMonth), [state, selectedMonth]);
  const setupComplete = isSetupComplete(state.settings);

  useEffect(() => {
    repository.save(state);
  }, [state]);

  useEffect(() => {
    if (!months.includes(selectedMonth)) setSelectedMonth(months[0]);
  }, [months, selectedMonth]);

  const openNewEntry = (type: TransactionType) => {
    if (!setupComplete) {
      window.alert('Finish setup before adding entries.');
      setView('settings');
      return;
    }
    setDrawer({ open: true, draft: makeDraft(type, state.settings, selectedMonth, state.transactions) });
  };

  const openEditEntry = (transaction: TreasuryTransaction) => {
    setDrawer({ open: true, draft: transactionToDraft(transaction, state.settings) });
  };

  const openRepeatEntry = (transaction: TreasuryTransaction) => {
    const draft = transactionToDraft(transaction, state.settings);
    setDrawer({
      open: true,
      draft: {
        ...draft,
        id: undefined,
        note: ''
      }
    });
  };

  const closeDrawer = () => {
    setDrawer((current) => ({ ...current, open: false }));
  };

  const saveEntry = (draft: EntryDraft, mode: SaveMode = 'close') => {
    if (!isSetupComplete(state.settings)) {
      window.alert('Finish setup before saving entries.');
      setView('settings');
      return;
    }

    if (draft.date.slice(0, 7) < state.settings.firstReportMonth) {
      window.alert(
        `This entry is before your opening balance date. Change the first report month or include this activity in opening balances.`
      );
      return;
    }

    const amount = safeNumber(draft.amount);
    if (amount <= 0) {
      window.alert('Amount must be greater than zero.');
      return;
    }
    if (!draft.description.trim()) {
      window.alert('Add a short description.');
      return;
    }

    const now = new Date().toISOString();
    const transaction: TreasuryTransaction = {
      id: draft.id ?? crypto.randomUUID?.() ?? String(Date.now()),
      type: draft.type,
      date: draft.date,
      amount,
      account: draft.account,
      paymentMethod: draft.paymentMethod,
      category: draft.type === 'expense' ? draft.category : undefined,
      description: draft.description.trim(),
      note: draft.note.trim() || undefined,
      missionFundExpense: draft.type === 'expense' ? draft.missionFundExpense : false,
      createdAt: draft.id ? state.transactions.find((item) => item.id === draft.id)?.createdAt ?? now : now,
      updatedAt: now
    };

    setState((current) => ({
      ...current,
      transactions: draft.id
        ? current.transactions.map((item) => (item.id === draft.id ? transaction : item))
        : [transaction, ...current.transactions]
    }));
    if (mode === 'again' && !draft.id) {
      setDrawer({
        open: true,
        draft: makeNextEntryDraft(draft)
      });
      return;
    }

    closeDrawer();
  };

  const deleteEntry = (id: string) => {
    if (!window.confirm('Delete this entry?')) return;
    setState((current) => ({
      ...current,
      transactions: current.transactions.filter((transaction) => transaction.id !== id)
    }));
  };

  const updateSettings = (patch: Partial<TreasurySettings>) => {
    const firstReportMonthChanged =
      patch.firstReportMonth !== undefined && patch.firstReportMonth !== state.settings.firstReportMonth;

    if (
      firstReportMonthChanged &&
      state.transactions.length > 0 &&
      !window.confirm('Changing the first report month changes how balances are calculated. Export a backup first if needed.')
    ) {
      return;
    }

    setState((current) => ({
      ...current,
      settings: {
        ...current.settings,
        ...patch,
        openingBalancesConfirmed:
          firstReportMonthChanged && patch.openingBalancesConfirmed === undefined
            ? false
            : patch.openingBalancesConfirmed ?? current.settings.openingBalancesConfirmed
      }
    }));

    if (patch.firstReportMonth) setSelectedMonth(patch.firstReportMonth);
  };

  const updateReportSettings = (patch: Partial<TreasurySettings['reportDisplay']>) => {
    setState((current) => ({
      ...current,
      settings: {
        ...current.settings,
        reportDisplay: {
          ...current.settings.reportDisplay,
          ...patch
        }
      }
    }));
  };

  const updateOpeningBalance = (account: keyof TreasurySettings['openingBalances'], value: number) => {
    setState((current) => ({
      ...current,
      settings: {
        ...current.settings,
        openingBalances: {
          ...current.settings.openingBalances,
          [account]: value
        },
        openingBalancesConfirmed:
          current.settings.openingBalances[account] === value ? current.settings.openingBalancesConfirmed : false
      }
    }));
  };

  const updateReportNotes = (notes: ReportNotes) => {
    setState((current) => ({
      ...current,
      reportNotes: {
        ...current.reportNotes,
        [selectedMonth]: notes
      }
    }));
  };

  const importBackup = (payload: unknown) => {
    const candidate = payload as { data?: unknown };
    setState(normalizeState(candidate.data ?? payload));
    setView('dashboard');
  };

  const startFresh = () => {
    if (!window.confirm('Clear all saved setup, entries, and notes? Export a backup first if needed.')) return;
    setState(structuredClone(freshState));
    setSelectedMonth(currentMonthKey());
  };

  return (
    <div className="app-shell">
      <aside className="sidebar no-print" aria-label="Primary">
        <div className="brand-block">
          <div className="brand-mark">S</div>
          <div>
            <strong>{state.settings.churchName || 'Church treasury'}</strong>
            <span>Monthly reporting</span>
          </div>
        </div>
        <nav className="nav-list">
          {navItems.map((item) => (
            <NavButton key={item.key} item={item} active={view === item.key} onClick={() => setView(item.key)} />
          ))}
        </nav>
        <StorageReminder compact />
      </aside>

      <main className="workspace">
        <header className="topbar no-print">
          <div className="topbar-title">
            <span>{formatMonth(selectedMonth)}</span>
            <h1>{view === 'report' ? 'Monthly report' : view === 'entries' ? 'Entries' : view === 'settings' ? 'Settings' : 'Dashboard'}</h1>
          </div>
          <div className="topbar-actions">
            <MonthSelect months={months} value={selectedMonth} onChange={setSelectedMonth} />
            <button className="btn btn-secondary" onClick={() => openNewEntry('income')} disabled={!setupComplete}>
              <Plus size={16} /> Money received
            </button>
            <button className="btn btn-primary" onClick={() => openNewEntry('expense')} disabled={!setupComplete}>
              <Plus size={16} /> Money spent
            </button>
          </div>
        </header>

        <div className="content-scroll">
          {!setupComplete && view !== 'settings' && <SetupGate settings={state.settings} onOpenSettings={() => setView('settings')} />}
          {setupComplete && view === 'dashboard' && (
            <Dashboard
              state={state}
              selectedMonth={selectedMonth}
              summary={summary}
              balances={balances}
              onViewReport={() => setView('report')}
              onNewEntry={openNewEntry}
              onRepeat={openRepeatEntry}
            />
          )}
          {setupComplete && view === 'entries' && (
            <EntriesView
              transactions={state.transactions}
              categories={state.settings.categories}
              selectedMonth={selectedMonth}
              onSelectedMonthChange={setSelectedMonth}
              months={months}
              onEdit={openEditEntry}
              onRepeat={openRepeatEntry}
              onDelete={deleteEntry}
              onNewEntry={openNewEntry}
            />
          )}
          {setupComplete && view === 'report' && (
            <ReportView
              state={state}
              selectedMonth={selectedMonth}
              months={months}
              summary={summary}
              onSelectedMonthChange={setSelectedMonth}
              onReportSettingsChange={updateReportSettings}
              onNotesChange={updateReportNotes}
            />
          )}
          {view === 'settings' && (
            <SettingsView
              state={state}
              months={months}
              onSettingsChange={updateSettings}
              onReportSettingsChange={updateReportSettings}
              onOpeningBalanceChange={updateOpeningBalance}
              onImportBackup={importBackup}
              onStartFresh={startFresh}
            />
          )}
        </div>
      </main>

      <nav className="bottom-nav no-print" aria-label="Primary">
        {navItems.map((item) => (
          <NavButton key={item.key} item={item} active={view === item.key} onClick={() => setView(item.key)} compact />
        ))}
      </nav>

      <EntryDrawer
        open={drawer.open}
        draft={drawer.draft}
        settings={state.settings}
        transactions={state.transactions}
        onChange={(draft) => setDrawer((current) => ({ ...current, draft }))}
        onClose={closeDrawer}
        onSave={saveEntry}
      />
    </div>
  );
}

function SetupGate({ settings, onOpenSettings }: { settings: TreasurySettings; onOpenSettings: () => void }) {
  const openingDate = formatOpeningBalanceDate(settings.firstReportMonth);

  return (
    <section className="view-stack">
      <div className="panel setup-gate">
        <div className="setup-gate-icon">
          <AlertTriangle size={20} />
        </div>
        <div>
          <span className="eyebrow">Setup required</span>
          <h2>Set the opening basis first</h2>
          <p>
            Choose the first report month, then enter balances at the start of that month. Transactions before that date should be part of the opening
            balances.
          </p>
        </div>
        <div className="setup-checklist">
          <span className={settings.churchName.trim() ? 'done' : ''}>Church name</span>
          <span className={settings.firstReportMonth ? 'done' : ''}>First report month</span>
          <span className={settings.missionFundRate > 0 ? 'done' : ''}>Mission Fund rate</span>
          <span className={settings.openingBalancesConfirmed ? 'done' : ''}>Balances from {openingDate}</span>
        </div>
        <button className="btn btn-primary" onClick={onOpenSettings}>
          <SettingsIcon size={16} /> Open settings
        </button>
      </div>
    </section>
  );
}

function Dashboard({
  state,
  selectedMonth,
  summary,
  balances,
  onViewReport,
  onNewEntry,
  onRepeat
}: {
  state: StoredTreasuryState;
  selectedMonth: string;
  summary: ReturnType<typeof calculateMonthlySummary>;
  balances: ReturnType<typeof calculateCurrentBalances>;
  onViewReport: () => void;
  onNewEntry: (type: TransactionType) => void;
  onRepeat: (transaction: TreasuryTransaction) => void;
}) {
  const recent = [...state.transactions].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 5);

  return (
    <section className="view-stack">
      <div className="mobile-hero no-print">
        <span>{formatMonth(selectedMonth)}</span>
        <h1>{state.settings.churchName || 'Church treasury'}</h1>
      </div>

      <div className="stat-grid">
        <MoneyCard label="Cash balance" value={balances.cash} icon={Wallet} />
        <MoneyCard label="Bank balance" value={balances.bank} icon={Landmark} />
        <MoneyCard label="Mission Fund" value={balances.mission} icon={Banknote} />
        <MoneyCard label="Monthly giving" value={summary.totalMonthlyIncome} icon={CheckCircle2} />
      </div>

      <div className="action-strip no-print">
        <button className="btn btn-primary" onClick={() => onNewEntry('income')}>
          <Plus size={16} /> Add received
        </button>
        <button className="btn btn-secondary" onClick={() => onNewEntry('expense')}>
          <Plus size={16} /> Add spent
        </button>
        <button className="btn btn-secondary" onClick={onViewReport}>
          <FileText size={16} /> View report
        </button>
      </div>

      <div className="grid-two">
        <div className="panel">
          <PanelHeader title="This month" kicker="Summary" />
          <div className="summary-list">
            <SummaryLine label="Cash received" value={summary.cashIncome} />
            <SummaryLine label="Cash spent" value={summary.cashExpenses} />
            <SummaryLine label="Bank received" value={summary.bankIncome} />
            <SummaryLine label="Bank spent" value={summary.bankExpenses} />
            <SummaryLine label={`Mission Fund ${state.settings.missionFundRate}%`} value={summary.missionFundAllocation} strong />
          </div>
        </div>

        <div className="panel">
          <PanelHeader title="Backup reminder" kicker="Device storage" />
          <StorageReminder />
        </div>
      </div>

      <div className="panel">
        <PanelHeader title="Recent entries" kicker={`${recent.length} shown`} />
        <EntryTable transactions={recent} onRepeat={onRepeat} compact />
      </div>
    </section>
  );
}

function EntriesView({
  transactions,
  categories,
  selectedMonth,
  onSelectedMonthChange,
  months,
  onEdit,
  onRepeat,
  onDelete,
  onNewEntry
}: {
  transactions: TreasuryTransaction[];
  categories: string[];
  selectedMonth: string;
  months: string[];
  onSelectedMonthChange: (month: string) => void;
  onEdit: (transaction: TreasuryTransaction) => void;
  onRepeat: (transaction: TreasuryTransaction) => void;
  onDelete: (id: string) => void;
  onNewEntry: (type: TransactionType) => void;
}) {
  const [query, setQuery] = useState('');
  const [type, setType] = useState<EntryFilter>('all');
  const [account, setAccount] = useState<AccountFilter>('all');
  const [category, setCategory] = useState('all');

  const filtered = useMemo(() => {
    return transactions
      .filter((transaction) => transaction.date.startsWith(selectedMonth))
      .filter((transaction) => (type === 'all' ? true : transaction.type === type))
      .filter((transaction) => (account === 'all' ? true : transaction.account === account))
      .filter((transaction) => (category === 'all' ? true : transaction.category === category))
      .filter((transaction) => {
        const haystack = [transaction.description, transaction.note, transaction.category].join(' ').toLowerCase();
        return haystack.includes(query.trim().toLowerCase());
      })
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [account, category, query, selectedMonth, transactions, type]);

  return (
    <section className="view-stack">
      <div className="section-row no-print">
        <div>
          <span className="eyebrow">Entries</span>
          <h2>Money in and out</h2>
        </div>
        <div className="button-row">
          <button className="btn btn-secondary" onClick={() => onNewEntry('income')}>
            <Plus size={16} /> Received
          </button>
          <button className="btn btn-primary" onClick={() => onNewEntry('expense')}>
            <Plus size={16} /> Spent
          </button>
          <button className="btn btn-secondary" onClick={() => downloadText(`entries-${selectedMonth}.csv`, transactionsToCsv(filtered), 'text/csv;charset=utf-8')}>
            <Download size={16} /> CSV
          </button>
        </div>
      </div>

      <div className="panel filters no-print">
        <div className="filter-grid">
          <label className="field">
            <span>Month</span>
            <MonthSelect months={months} value={selectedMonth} onChange={onSelectedMonthChange} />
          </label>
          <label className="field">
            <span>Type</span>
            <select value={type} onChange={(event) => setType(event.target.value as EntryFilter)}>
              <option value="all">All</option>
              <option value="income">Money received</option>
              <option value="expense">Money spent</option>
            </select>
          </label>
          <label className="field">
            <span>Account</span>
            <select value={account} onChange={(event) => setAccount(event.target.value as AccountFilter)}>
              <option value="all">All</option>
              <option value="cash">Cash</option>
              <option value="bank">Bank</option>
            </select>
          </label>
          <label className="field">
            <span>Category</span>
            <select value={category} onChange={(event) => setCategory(event.target.value)}>
              <option value="all">All</option>
              {categories.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
          <label className="field search-field">
            <span>Search</span>
            <div className="search-box">
              <Search size={16} />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Description or note" />
            </div>
          </label>
        </div>
      </div>

      <div className="panel">
        <PanelHeader title="Filtered entries" kicker={`${filtered.length} shown`} icon={Filter} />
        {filtered.length ? (
          <EntryTable transactions={filtered} onEdit={onEdit} onRepeat={onRepeat} onDelete={onDelete} />
        ) : (
          <EmptyState title="No entries" body="Adjust filters or add money received or spent." />
        )}
      </div>
    </section>
  );
}

function ReportView({
  state,
  selectedMonth,
  months,
  summary,
  onSelectedMonthChange,
  onReportSettingsChange,
  onNotesChange
}: {
  state: StoredTreasuryState;
  selectedMonth: string;
  months: string[];
  summary: ReturnType<typeof calculateMonthlySummary>;
  onSelectedMonthChange: (month: string) => void;
  onReportSettingsChange: (patch: Partial<TreasurySettings['reportDisplay']>) => void;
  onNotesChange: (notes: ReportNotes) => void;
}) {
  const display = state.settings.reportDisplay;
  const notes = state.reportNotes[selectedMonth] ?? { cash: '', bank: '', mission: '' };
  const [exportStartMonth, setExportStartMonth] = useState(selectedMonth);
  const [exportEndMonth, setExportEndMonth] = useState(selectedMonth);
  const [isSharingPdf, setIsSharingPdf] = useState(false);

  useEffect(() => {
    setExportStartMonth(selectedMonth);
    setExportEndMonth(selectedMonth);
  }, [selectedMonth]);

  useEffect(() => {
    void import('pdf-lib');
  }, []);

  const exportMonthRange = () => {
    const selectedMonths = getMonthRange(exportStartMonth, exportEndMonth);
    const summaries = selectedMonths.map((month) => calculateMonthlySummary(state, month));
    downloadText(
      `monthly-reports-${selectedMonths[0]}-to-${selectedMonths[selectedMonths.length - 1]}.csv`,
      monthlyReportsToCsv(summaries, display.showBalanceColumn, display.showNotesColumn),
      'text/csv;charset=utf-8'
    );
  };

  const applySuggestedNotes = () => {
    onNotesChange(suggestReportNotes(selectedMonth, state.transactions, state.settings.missionFundRate));
  };

  const sharePdf = async () => {
    setIsSharingPdf(true);
    try {
      const blob = await createMonthlyReportPdfBlob({
        churchName: state.settings.churchName,
        display,
        month: selectedMonth,
        summary
      });
      const filename = reportPdfFilename(state.settings.churchName, selectedMonth);
      const file = new File([blob], filename, { type: 'application/pdf' });
      const shareData = {
        title: `${state.settings.churchName || 'SGRBC'} ${formatMonth(selectedMonth)} report`,
        text: `${formatMonth(selectedMonth)} monthly finance report`,
        files: [file]
      };

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share(shareData);
          return;
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') return;
        }
      }

      downloadBlob(filename, blob);
    } finally {
      setIsSharingPdf(false);
    }
  };

  return (
    <section className="view-stack report-surface">
      <div className="section-row no-print">
        <div>
          <span className="eyebrow">Monthly report</span>
          <h2>{formatMonth(selectedMonth)}</h2>
        </div>
        <div className="button-row">
          <MonthSelect months={months} value={selectedMonth} onChange={onSelectedMonthChange} />
          <button className="btn btn-secondary" onClick={applySuggestedNotes}>
            <RefreshCw size={16} /> Suggest notes
          </button>
          <button
            className="btn btn-secondary"
            onClick={() =>
              downloadText(
                `monthly-report-${selectedMonth}.csv`,
                monthlyReportToCsv(summary, display.showBalanceColumn, display.showNotesColumn),
                'text/csv;charset=utf-8'
              )
            }
          >
            <Download size={16} /> CSV
          </button>
          <button className="btn btn-primary" onClick={sharePdf} disabled={isSharingPdf}>
            <Share2 size={16} /> {isSharingPdf ? 'Preparing PDF' : 'Share PDF'}
          </button>
        </div>
      </div>

      <div className="panel report-panel">
        <MonthlyReportPreview
          churchName={state.settings.churchName}
          display={display}
          month={selectedMonth}
          summary={summary}
        />
      </div>

      <div className="grid-two no-print">
        <div className="panel">
          <PanelHeader title="Row notes" kicker="Editable" />
          <div className="form-grid single">
            <Textarea label="Cash Account note" value={notes.cash} onChange={(cash) => onNotesChange({ ...notes, cash })} />
            <Textarea label="Bank Account note" value={notes.bank} onChange={(bank) => onNotesChange({ ...notes, bank })} />
            <Textarea label="Mission Fund note" value={notes.mission} onChange={(mission) => onNotesChange({ ...notes, mission })} />
          </div>
        </div>

        <div className="panel">
          <PanelHeader title="Report display" kicker="Member view" />
          <div className="toggle-list">
            <Toggle label="Show balance column" checked={display.showBalanceColumn} onChange={(showBalanceColumn) => onReportSettingsChange({ showBalanceColumn })} />
            <Toggle label="Show Cash balance" checked={display.showCashBalance} onChange={(showCashBalance) => onReportSettingsChange({ showCashBalance })} />
            <Toggle label="Show Bank balance" checked={display.showBankBalance} onChange={(showBankBalance) => onReportSettingsChange({ showBankBalance })} />
            <Toggle label="Show Mission Fund balance" checked={display.showMissionBalance} onChange={(showMissionBalance) => onReportSettingsChange({ showMissionBalance })} />
            <Toggle label="Show notes column" checked={display.showNotesColumn} onChange={(showNotesColumn) => onReportSettingsChange({ showNotesColumn })} />
          </div>
        </div>
      </div>

      <div className="panel no-print">
        <PanelHeader title="Export months" kicker="Separate reports" />
        <div className="form-grid export-grid">
          <label className="field">
            <span>From</span>
            <MonthSelect months={months} value={exportStartMonth} onChange={setExportStartMonth} />
          </label>
          <label className="field">
            <span>Through</span>
            <MonthSelect months={months} value={exportEndMonth} onChange={setExportEndMonth} />
          </label>
          <div className="field field-action">
            <span>Export</span>
            <button className="btn btn-secondary" onClick={exportMonthRange}>
              <Download size={16} /> Export months
            </button>
          </div>
        </div>
      </div>

      <div className="panel explanation no-print">
        <PanelHeader title="Calculation" kicker="Plain language" />
        <div className="summary-list">
          <SummaryLine label="Total monthly giving" value={summary.totalMonthlyIncome} />
          <SummaryLine label={`Mission Fund set aside (${state.settings.missionFundRate}%)`} value={summary.missionFundAllocation} />
          <SummaryLine label="Mission Fund spending marked this month" value={summary.missionFundExpenses} />
        </div>
      </div>
    </section>
  );
}

function MonthlyReportPreview({
  churchName,
  display,
  month,
  summary
}: {
  churchName: string;
  display: TreasurySettings['reportDisplay'];
  month: string;
  summary: ReturnType<typeof calculateMonthlySummary>;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const documentRef = useRef<HTMLElement>(null);
  const [preview, setPreview] = useState({ height: 0, offset: 0, scale: 1 });

  useEffect(() => {
    const updatePreview = () => {
      const viewport = viewportRef.current;
      const documentNode = documentRef.current;
      if (!viewport || !documentNode) return;

      const viewportWidth = viewport.clientWidth;
      const scale = Math.min(1, viewportWidth / REPORT_PREVIEW_WIDTH);
      const height = documentNode.scrollHeight * scale;
      const offset = Math.max(0, (viewportWidth - REPORT_PREVIEW_WIDTH * scale) / 2);

      setPreview((current) => {
        const unchanged =
          Math.abs(current.scale - scale) < 0.001 &&
          Math.abs(current.height - height) < 1 &&
          Math.abs(current.offset - offset) < 1;
        return unchanged ? current : { height, offset, scale };
      });
    };

    updatePreview();

    const resizeObserver = new ResizeObserver(updatePreview);
    if (viewportRef.current) resizeObserver.observe(viewportRef.current);
    if (documentRef.current) resizeObserver.observe(documentRef.current);
    window.addEventListener('resize', updatePreview);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updatePreview);
    };
  }, [churchName, display, month, summary]);

  return (
    <div
      ref={viewportRef}
      className="report-preview-viewport"
      style={{ height: preview.height ? `${preview.height}px` : undefined }}
    >
      <div
        className="report-preview-stage"
        style={{
          marginLeft: `${preview.offset}px`,
          transform: `scale(${preview.scale})`
        }}
      >
        <MonthlyReportDocument
          churchName={churchName}
          display={display}
          documentRef={documentRef}
          month={month}
          summary={summary}
        />
      </div>
    </div>
  );
}

function MonthlyReportDocument({
  churchName,
  display,
  documentRef,
  month,
  summary
}: {
  churchName: string;
  display: TreasurySettings['reportDisplay'];
  documentRef?: RefObject<HTMLElement | null>;
  month: string;
  summary: ReturnType<typeof calculateMonthlySummary>;
}) {
  const cashAndBankClosing = summary.cashClosingBalance + summary.bankClosingBalance;

  return (
    <article ref={documentRef} className="monthly-report-doc" aria-label={`${formatMonth(month)} monthly finance report`}>
      <header className="monthly-report-title">
        <span>{churchName}</span>
        <h1>Monthly finance report</h1>
        <p>{formatMonth(month)}</p>
      </header>

      <section className="report-summary-card" aria-labelledby="report-summary-title">
        <h2 id="report-summary-title">Summary</h2>
        <div className="report-summary-hero">
          <span>Cash and bank closing</span>
          <strong className="report-money">{formatMoney(cashAndBankClosing)}</strong>
        </div>
        <div className="report-rule" />
        <ReportMetric label="Total received" value={summary.totalMonthlyIncome} />
        <ReportMetric label="Total spent" value={summary.cashExpenses + summary.bankExpenses} />
        <div className="report-rule" />
        <ReportMetric label="Mission Fund balance" value={summary.missionFundClosingBalance} tone="positive" />
      </section>

      <section className="report-detail" aria-labelledby="report-detail-title">
        <h2 id="report-detail-title">Account detail</h2>
        {summary.rows.map((row) => (
          <ReportAccountSection key={row.key} display={display} row={row} />
        ))}
      </section>
    </article>
  );
}

function ReportAccountSection({
  display,
  row
}: {
  display: TreasurySettings['reportDisplay'];
  row: MonthlyReportRow;
}) {
  const shouldHideBalance = !display.showBalanceColumn || !row.showBalance;
  const shouldShowNote = display.showNotesColumn && row.note.trim();

  return (
    <section className="report-account-block">
      <h3>{row.account}</h3>
      <div className="report-rule" />
      <div className="report-account-lines">
        <ReportMetric label="Closing balance" value={row.balance} tone={getReportMoneyTone(row.balance)} hidden={shouldHideBalance} />
        <ReportMetric label="Received" value={row.income} />
        <ReportMetric label="Spent" value={row.expense} />
      </div>
      {shouldShowNote && (
        <>
          <div className="report-rule" />
          <div className="report-note-row">
            <span>Note</span>
            <p>{row.note}</p>
          </div>
        </>
      )}
    </section>
  );
}

function ReportMetric({ label, value, tone, hidden = false }: { label: string; value: number; tone?: 'positive' | 'negative'; hidden?: boolean }) {
  return (
    <div className="report-metric">
      <span>{label}</span>
      <strong className={`report-money${tone && !hidden ? ` ${tone}` : ''}${hidden ? ' hidden' : ''}`}>
        {hidden ? 'Hidden' : formatMoney(value)}
      </strong>
    </div>
  );
}

function getReportMoneyTone(value: number) {
  if (value < 0) return 'negative';
  if (value > 0) return 'positive';
  return undefined;
}

function reportPdfFilename(churchName: string, month: string) {
  const safeName = (churchName || 'sgrbc')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${safeName || 'sgrbc'}-monthly-report-${month}.pdf`;
}

function SettingsView({
  state,
  months,
  onSettingsChange,
  onReportSettingsChange,
  onOpeningBalanceChange,
  onImportBackup,
  onStartFresh
}: {
  state: StoredTreasuryState;
  months: string[];
  onSettingsChange: (patch: Partial<TreasurySettings>) => void;
  onReportSettingsChange: (patch: Partial<TreasurySettings['reportDisplay']>) => void;
  onOpeningBalanceChange: (account: keyof TreasurySettings['openingBalances'], value: number) => void;
  onImportBackup: (payload: unknown) => void;
  onStartFresh: () => void;
}) {
  const [newCategory, setNewCategory] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const openingDate = formatOpeningBalanceDate(state.settings.firstReportMonth);
  const setupComplete = isSetupComplete(state.settings);

  const addCategory = () => {
    const value = newCategory.trim();
    if (!value || state.settings.categories.includes(value)) return;
    onSettingsChange({ categories: [...state.settings.categories, value] });
    setNewCategory('');
  };

  const removeCategory = (category: string) => {
    onSettingsChange({ categories: state.settings.categories.filter((item) => item !== category) });
  };

  const handleImport = async (file: File | undefined) => {
    if (!file) return;
    const text = await file.text();
    onImportBackup(JSON.parse(text));
  };

  return (
    <section className="view-stack">
      <div className="section-row">
        <div>
          <span className="eyebrow">Setup</span>
          <h2>Church settings</h2>
        </div>
      </div>

      <div className="panel">
        <PanelHeader title="Basic details" kicker="Monthly report" />
        <div className="form-grid">
          <label className="field">
            <span>Church name</span>
            <input value={state.settings.churchName} onChange={(event) => onSettingsChange({ churchName: event.target.value })} />
          </label>
          <label className="field">
            <span>Currency</span>
            <input value={state.settings.currency} disabled />
          </label>
          <NumberInput
            label="Mission Fund rate"
            value={state.settings.missionFundRate}
            onChange={(missionFundRate) => onSettingsChange({ missionFundRate })}
          />
        </div>
      </div>

      <div className="panel">
        <PanelHeader title="Opening basis" kicker={setupComplete ? 'Confirmed' : 'Required'} />
        <div className="form-grid">
          <label className="field">
            <span>First report month</span>
            <select
              value={state.settings.firstReportMonth}
              onChange={(event) => onSettingsChange({ firstReportMonth: event.target.value })}
            >
              <option value="">Choose month</option>
              {months.map((month) => (
                <option key={month} value={month}>
                  {formatMonth(month)}
                </option>
              ))}
            </select>
          </label>
          <NumberInput
            label={`Cash at start of ${openingDate}`}
            value={state.settings.openingBalances.cash}
            allowNegative
            onChange={(value) => onOpeningBalanceChange('cash', value)}
          />
          <NumberInput
            label={`Bank at start of ${openingDate}`}
            value={state.settings.openingBalances.bank}
            allowNegative
            onChange={(value) => onOpeningBalanceChange('bank', value)}
          />
          <NumberInput
            label={`Mission Fund at start of ${openingDate}`}
            value={state.settings.openingBalances.mission}
            allowNegative
            onChange={(value) => onOpeningBalanceChange('mission', value)}
          />
        </div>
        <div className="setup-confirm">
          <div>
            <strong>{state.settings.openingBalancesConfirmed ? 'Opening balances confirmed' : 'Confirm opening balances'}</strong>
            <span>These balances are the starting point for every report from {openingDate} onward.</span>
          </div>
          <button
            className={`btn ${state.settings.openingBalancesConfirmed ? 'btn-secondary' : 'btn-primary'}`}
            disabled={!state.settings.firstReportMonth}
            onClick={() => onSettingsChange({ openingBalancesConfirmed: true })}
          >
            {state.settings.openingBalancesConfirmed ? 'Confirmed' : 'Confirm'}
          </button>
        </div>
      </div>

      <div className="grid-two">
        <div className="panel">
          <PanelHeader title="Report display" kicker="Default view" />
          <div className="toggle-list">
            <Toggle label="Show balance column" checked={state.settings.reportDisplay.showBalanceColumn} onChange={(showBalanceColumn) => onReportSettingsChange({ showBalanceColumn })} />
            <Toggle label="Show Cash balance" checked={state.settings.reportDisplay.showCashBalance} onChange={(showCashBalance) => onReportSettingsChange({ showCashBalance })} />
            <Toggle label="Show Bank balance" checked={state.settings.reportDisplay.showBankBalance} onChange={(showBankBalance) => onReportSettingsChange({ showBankBalance })} />
            <Toggle label="Show Mission Fund balance" checked={state.settings.reportDisplay.showMissionBalance} onChange={(showMissionBalance) => onReportSettingsChange({ showMissionBalance })} />
            <Toggle label="Show notes column" checked={state.settings.reportDisplay.showNotesColumn} onChange={(showNotesColumn) => onReportSettingsChange({ showNotesColumn })} />
          </div>
        </div>

        <div className="panel">
          <PanelHeader title="Data care" kicker="Local storage" icon={Database} />
          <StorageReminder tone="attention" />
          <div className="button-row stack">
            <button className="btn btn-secondary" onClick={() => downloadText('sgrbc-treasury-backup.json', backupToJson(state), 'application/json;charset=utf-8')}>
              <Download size={16} /> Export backup
            </button>
            <button className="btn btn-secondary" onClick={() => fileInputRef.current?.click()}>
              <Upload size={16} /> Import backup
            </button>
            <button className="btn btn-secondary" onClick={onStartFresh}>
              <Trash2 size={16} /> Start fresh
            </button>
            <input ref={fileInputRef} className="hidden-input" type="file" accept="application/json,.json" onChange={(event) => handleImport(event.target.files?.[0])} />
          </div>
        </div>
      </div>

      <div className="panel">
        <PanelHeader title="Expense categories" kicker={`${state.settings.categories.length} saved`} />
        <div className="category-editor">
          <div className="category-input">
            <input value={newCategory} onChange={(event) => setNewCategory(event.target.value)} placeholder="Add category" />
            <button className="btn btn-primary" onClick={addCategory}>
              <Plus size={16} /> Add
            </button>
          </div>
          <div className="category-list">
            {state.settings.categories.map((category) => (
              <span className="category-chip" key={category}>
                {category}
                <button aria-label={`Remove ${category}`} onClick={() => removeCategory(category)}>
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function EntryDrawer({
  open,
  draft,
  settings,
  transactions,
  onChange,
  onClose,
  onSave
}: {
  open: boolean;
  draft: EntryDraft;
  settings: TreasurySettings;
  transactions: TreasuryTransaction[];
  onChange: (draft: EntryDraft) => void;
  onClose: () => void;
  onSave: (draft: EntryDraft, mode?: SaveMode) => void;
}) {
  const title = draft.id ? 'Edit entry' : draft.type === 'income' ? 'New money received' : 'New money spent';
  const beforeOpeningDate = Boolean(settings.firstReportMonth && draft.date.slice(0, 7) < settings.firstReportMonth);
  const quickDescriptions = getQuickDescriptions(draft, transactions);

  return (
    <>
      <div className={`scrim ${open ? 'open' : ''}`} onClick={onClose} />
      <aside className={`drawer ${open ? 'open' : ''}`} aria-hidden={!open} aria-label={title}>
        <div className="drawer-head">
          <div>
            <span className="eyebrow">Entry</span>
            <h2>{title}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="drawer-body">
          <div className="segmented">
            <button
              className={draft.type === 'income' ? 'active' : ''}
              onClick={() =>
                onChange({
                  ...draft,
                  type: 'income',
                  paymentMethod: sanitizePaymentMethod('income', draft.paymentMethod),
                  missionFundExpense: false
                })
              }
            >
              Money received
            </button>
            <button className={draft.type === 'expense' ? 'active' : ''} onClick={() => onChange({ ...draft, type: 'expense' })}>
              Money spent
            </button>
          </div>

          <div className="form-grid single">
            <DateInput value={draft.date} firstReportMonth={settings.firstReportMonth} onChange={(date) => onChange({ ...draft, date })} />
            {beforeOpeningDate && (
              <div className="inline-warning">
                <AlertTriangle size={16} />
                <span>This date is before opening balances from {formatOpeningBalanceDate(settings.firstReportMonth)}.</span>
              </div>
            )}
            <MoneyTextInput label="Amount" value={draft.amount} onChange={(amount) => onChange({ ...draft, amount })} />
            <label className="field">
              <span>{draft.type === 'income' ? 'Received into' : 'Paid from'}</span>
              <select value={draft.account} onChange={(event) => onChange({ ...draft, account: event.target.value as AccountKey })}>
                <option value="cash">Cash Account</option>
                <option value="bank">Bank Account</option>
              </select>
            </label>
            <label className="field">
              <span>Payment method</span>
              <select value={draft.paymentMethod} onChange={(event) => onChange({ ...draft, paymentMethod: event.target.value as PaymentMethod })}>
                {paymentMethods
                  .filter((method) => draft.type === 'expense' || method !== 'cheque')
                  .map((method) => (
                    <option key={method} value={method}>
                      {paymentMethodLabels[method]}
                    </option>
                  ))}
              </select>
            </label>
            {draft.type === 'expense' && (
              <label className="field">
                <span>Category</span>
                <select value={draft.category} onChange={(event) => onChange({ ...draft, category: event.target.value })}>
                  {settings.categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="field">
              <span>Description</span>
              <input value={draft.description} onChange={(event) => onChange({ ...draft, description: event.target.value })} placeholder="Short description" />
            </label>
            {quickDescriptions.length > 0 && (
              <div className="quick-chip-row" aria-label="Recent descriptions">
                {quickDescriptions.map((description) => (
                  <button
                    key={description}
                    type="button"
                    className={`quick-chip ${draft.description === description ? 'active' : ''}`}
                    onClick={() => onChange({ ...draft, description })}
                  >
                    {description}
                  </button>
                ))}
              </div>
            )}
            <Textarea label="Optional note" value={draft.note} onChange={(note) => onChange({ ...draft, note })} />
            {draft.type === 'expense' && (
              <Toggle
                label="Mark as Mission Fund spending"
                checked={draft.missionFundExpense}
                onChange={(missionFundExpense) => onChange({ ...draft, missionFundExpense })}
              />
            )}
          </div>
        </div>

        <div className="drawer-foot">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          {!draft.id && (
            <button className="btn btn-secondary" onClick={() => onSave(draft, 'again')}>
              <Plus size={16} /> Save & add
            </button>
          )}
          <button className="btn btn-primary" onClick={() => onSave(draft)}>
            <Save size={16} /> Save entry
          </button>
        </div>
      </aside>
    </>
  );
}

function EntryTable({
  transactions,
  onEdit,
  onRepeat,
  onDelete,
  compact = false
}: {
  transactions: TreasuryTransaction[];
  onEdit?: (transaction: TreasuryTransaction) => void;
  onRepeat?: (transaction: TreasuryTransaction) => void;
  onDelete?: (id: string) => void;
  compact?: boolean;
}) {
  if (!transactions.length) return <EmptyState title="No entries" body="Add money received or spent." />;

  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Details</th>
            {!compact && <th>Method</th>}
            <th>Account</th>
            <th className="right">Amount</th>
            {(onEdit || onRepeat || onDelete) && <th className="right">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {transactions.map((transaction) => (
            <tr key={transaction.id}>
              <td className="num nowrap">{transaction.date}</td>
              <td>
                <div className="entry-detail">
                  <strong>{transaction.description}</strong>
                  <span>{transaction.type === 'expense' ? transaction.category : typeLabels[transaction.type]}</span>
                </div>
              </td>
              {!compact && <td>{paymentMethodLabels[transaction.paymentMethod]}</td>}
              <td>{shortAccountLabels[transaction.account]}</td>
              <td className={`right num ${transaction.type === 'income' ? 'positive' : ''}`}>
                {transaction.type === 'income' ? '+' : '-'}
                {formatMoney(transaction.amount)}
              </td>
              {(onEdit || onRepeat || onDelete) && (
                <td className="right">
                  <div className="row-actions">
                    {onRepeat && (
                      <button className="icon-button" onClick={() => onRepeat(transaction)} aria-label="Repeat entry">
                        <Copy size={16} />
                      </button>
                    )}
                    {onEdit && (
                      <button className="icon-button" onClick={() => onEdit(transaction)} aria-label="Edit entry">
                        <Pencil size={16} />
                      </button>
                    )}
                    {onDelete && (
                      <button className="icon-button danger" onClick={() => onDelete(transaction.id)} aria-label="Delete entry">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NavButton({
  item,
  active,
  onClick,
  compact = false
}: {
  item: { key: ViewKey; label: string; icon: typeof Home };
  active: boolean;
  onClick: () => void;
  compact?: boolean;
}) {
  const Icon = item.icon;
  return (
    <button className={`nav-button ${active ? 'active' : ''} ${compact ? 'compact' : ''}`} onClick={onClick}>
      <Icon size={18} />
      <span>{compact && item.key === 'report' ? 'Report' : item.label}</span>
    </button>
  );
}

function MoneyCard({ label, value, icon: Icon }: { label: string; value: number; icon: typeof Wallet }) {
  return (
    <div className="stat-card">
      <div className="stat-card-top">
        <span>{label}</span>
        <div className="stat-icon">
          <Icon size={16} />
        </div>
      </div>
      <strong className="num">{formatMoney(value)}</strong>
    </div>
  );
}

function SummaryLine({ label, value, strong = false }: { label: string; value: number; strong?: boolean }) {
  return (
    <div className={`summary-line ${strong ? 'strong' : ''}`}>
      <span>{label}</span>
      <strong className="num">{formatMoney(value)}</strong>
    </div>
  );
}

function PanelHeader({ title, kicker, icon: Icon }: { title: string; kicker?: string; icon?: typeof Home }) {
  return (
    <div className="panel-head">
      <div>
        {kicker && <span>{kicker}</span>}
        <h3>{title}</h3>
      </div>
      {Icon && (
        <div className="panel-icon">
          <Icon size={16} />
        </div>
      )}
    </div>
  );
}

function MonthSelect({ months, value, onChange }: { months: string[]; value: string; onChange: (month: string) => void }) {
  return (
    <select className="month-select" value={value} onChange={(event) => onChange(event.target.value)}>
      {months.map((month) => (
        <option key={month} value={month}>
          {formatMonth(month)}
        </option>
      ))}
    </select>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className="toggle-row">
      <span>{label}</span>
      <button type="button" className={`switch ${checked ? 'on' : ''}`} aria-pressed={checked} onClick={() => onChange(!checked)}>
        <span />
      </button>
    </label>
  );
}

function Textarea({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="field">
      <span>{label}</span>
      <textarea value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function DateInput({
  value,
  firstReportMonth,
  onChange
}: {
  value: string;
  firstReportMonth: string;
  onChange: (value: string) => void;
}) {
  const { year, month, day } = parseDateParts(value);
  const days = getDaysInMonth(year, month);
  const years = getDateYearOptions(value, firstReportMonth);

  const updateDate = (patch: Partial<{ year: number; month: number; day: number }>) => {
    const nextYear = patch.year ?? year;
    const nextMonth = patch.month ?? month;
    const nextDay = Math.min(patch.day ?? day, getDaysInMonth(nextYear, nextMonth));
    onChange(formatDateInputValue(nextYear, nextMonth, nextDay));
  };

  return (
    <div className="field">
      <span>Date</span>
      <div className="date-control" aria-label="Date">
        <select className="num date-day" aria-label="Day" value={day} onChange={(event) => updateDate({ day: Number(event.target.value) })}>
          {Array.from({ length: days }, (_, index) => index + 1).map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <select aria-label="Month" value={month} onChange={(event) => updateDate({ month: Number(event.target.value) })}>
          {monthOptions.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <select className="num date-year" aria-label="Year" value={year} onChange={(event) => updateDate({ year: Number(event.target.value) })}>
          {years.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function NumberInput({
  label,
  value,
  allowNegative = false,
  onChange
}: {
  label: string;
  value: number;
  allowNegative?: boolean;
  onChange: (value: number) => void;
}) {
  const labelId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [textValue, setTextValue] = useState(() => formatNumberInputValue(value));

  useEffect(() => {
    if (document.activeElement !== inputRef.current) setTextValue(formatNumberInputValue(value));
  }, [value]);

  const handleChange = (rawValue: string) => {
    if (!isAllowedNumberDraft(rawValue, allowNegative)) return;

    setTextValue(rawValue);
    if (isCompleteNumberText(rawValue)) onChange(Number(rawValue));
  };

  const handleBlur = () => {
    const nextValue = isCompleteNumberText(textValue) ? Number(textValue) : 0;
    onChange(nextValue);
    setTextValue(formatNumberInputValue(nextValue));
  };

  const toggleSign = () => {
    const trimmed = textValue.trim();
    const nextText = trimmed.startsWith('-') ? trimmed.slice(1) : trimmed === '0' || trimmed === '' ? '-' : `-${trimmed}`;
    setTextValue(nextText);
    if (isCompleteNumberText(nextText)) onChange(Number(nextText));
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  return (
    <div className="field">
      <span id={labelId}>{label}</span>
      <div className={`number-control ${allowNegative ? 'with-sign' : ''}`}>
        {allowNegative && (
          <button
            type="button"
            className="sign-toggle"
            aria-label={`Toggle sign for ${label}`}
            onPointerDown={(event) => event.preventDefault()}
            onClick={toggleSign}
          >
            {textValue.trim().startsWith('-') ? '-' : '+'}
          </button>
        )}
        <input
          ref={inputRef}
          className="num"
          type="text"
          inputMode="decimal"
          aria-labelledby={labelId}
          value={textValue}
          onBlur={handleBlur}
          onChange={(event) => handleChange(event.target.value)}
        />
      </div>
    </div>
  );
}

function formatNumberInputValue(value: number) {
  return Number.isFinite(value) ? String(value) : '0';
}

function isAllowedNumberDraft(value: string, allowNegative: boolean) {
  const pattern = allowNegative ? /^-?\d*(?:\.\d*)?$/ : /^\d*(?:\.\d*)?$/;
  return pattern.test(value);
}

function isCompleteNumberText(value: string) {
  return /^-?(?:\d+|\d*\.\d+)$/.test(value.trim());
}

function MoneyTextInput({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        className="num"
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(event) => {
          if (isAllowedNumberDraft(event.target.value, false)) onChange(event.target.value);
        }}
        placeholder="0.00"
      />
    </label>
  );
}

function StorageReminder({ compact = false, tone = 'quiet' }: { compact?: boolean; tone?: 'quiet' | 'attention' }) {
  return (
    <div className={`storage-reminder ${compact ? 'compact' : ''} ${tone === 'attention' ? 'attention' : ''}`}>
      <AlertTriangle size={compact ? 15 : 18} />
      <p>Data is saved on this device and browser. Export a backup regularly.</p>
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="empty-state">
      <FileText size={24} />
      <strong>{title}</strong>
      <span>{body}</span>
    </div>
  );
}

function isSetupComplete(settings: TreasurySettings) {
  return Boolean(
    settings.churchName.trim() &&
      settings.firstReportMonth &&
      settings.missionFundRate > 0 &&
      settings.openingBalancesConfirmed
  );
}

function formatOpeningBalanceDate(month: string) {
  if (!month) return 'first report month';
  const [year, index] = month.split('-').map(Number);
  return new Intl.DateTimeFormat('en-GH', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(year, index - 1, 1));
}

function parseDateParts(value: string) {
  const [fallbackYear, fallbackMonth, fallbackDay] = todayInputValue().split('-').map(Number);
  const [rawYear, rawMonth, rawDay] = value.split('-').map(Number);
  const year = Number.isInteger(rawYear) ? rawYear : fallbackYear;
  const month = clamp(Number.isInteger(rawMonth) ? rawMonth : fallbackMonth, 1, 12);
  const day = clamp(Number.isInteger(rawDay) ? rawDay : fallbackDay, 1, getDaysInMonth(year, month));
  return { year, month, day };
}

function getDateYearOptions(value: string, firstReportMonth: string) {
  const selectedYear = parseDateParts(value).year;
  const currentYear = parseDateParts(todayInputValue()).year;
  const firstYear = firstReportMonth ? Number(firstReportMonth.slice(0, 4)) : currentYear;
  const start = Math.min(selectedYear, firstYear, currentYear - 2);
  const end = Math.max(selectedYear, currentYear + 2);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month, 0).getDate();
}

function formatDateInputValue(year: number, month: number, day: number) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function makeDraft(
  type: TransactionType,
  settings: TreasurySettings,
  selectedMonth: string,
  transactions: TreasuryTransaction[]
): EntryDraft {
  const recentSimilar = findRecentSimilarTransaction(type, selectedMonth, transactions);

  return {
    type,
    date: getDefaultEntryDate(type, selectedMonth, transactions),
    amount: '',
    account: recentSimilar?.account ?? 'cash',
    paymentMethod: sanitizePaymentMethod(type, recentSimilar?.paymentMethod ?? 'cash'),
    category: recentSimilar?.category ?? settings.categories[0] ?? 'Other',
    description: '',
    note: '',
    missionFundExpense: type === 'expense' ? Boolean(recentSimilar?.missionFundExpense) : false
  };
}

function makeNextEntryDraft(draft: EntryDraft): EntryDraft {
  return {
    ...draft,
    id: undefined,
    amount: '',
    description: '',
    note: ''
  };
}

function transactionToDraft(transaction: TreasuryTransaction, settings: TreasurySettings): EntryDraft {
  return {
    id: transaction.id,
    type: transaction.type,
    date: transaction.date,
    amount: String(transaction.amount),
    account: transaction.account,
    paymentMethod: transaction.paymentMethod,
    category: transaction.category ?? settings.categories[0] ?? 'Other',
    description: transaction.description,
    note: transaction.note ?? '',
    missionFundExpense: Boolean(transaction.missionFundExpense)
  };
}

function findRecentSimilarTransaction(type: TransactionType, selectedMonth: string, transactions: TreasuryTransaction[]) {
  return [...transactions]
    .filter((transaction) => transaction.type === type)
    .sort((a, b) => `${b.date}-${b.updatedAt}`.localeCompare(`${a.date}-${a.updatedAt}`))
    .find((transaction) => transaction.date.startsWith(selectedMonth)) ??
    [...transactions]
      .filter((transaction) => transaction.type === type)
      .sort((a, b) => `${b.date}-${b.updatedAt}`.localeCompare(`${a.date}-${a.updatedAt}`))[0];
}

function getDefaultEntryDate(type: TransactionType, selectedMonth: string, transactions: TreasuryTransaction[]) {
  const recentSameMonth = findRecentSimilarTransaction(type, selectedMonth, transactions);
  if (recentSameMonth?.date.startsWith(selectedMonth)) return recentSameMonth.date;
  if (selectedMonth === currentMonthKey()) return todayInputValue();
  return `${selectedMonth}-01`;
}

function sanitizePaymentMethod(type: TransactionType, method: PaymentMethod): PaymentMethod {
  if (type === 'income' && method === 'cheque') return 'cash';
  return method;
}

function getQuickDescriptions(draft: EntryDraft, transactions: TreasuryTransaction[]) {
  const recentMatchingType = getRecentDescriptions(
    transactions.filter((transaction) => transaction.type === draft.type)
  );

  if (draft.type === 'income') return recentMatchingType;

  const recentMatchingCategory = getRecentDescriptions(
    transactions.filter((transaction) => transaction.type === 'expense' && transaction.category === draft.category)
  );

  return combineDescriptions(recentMatchingCategory, recentMatchingType);
}

function getRecentDescriptions(transactions: TreasuryTransaction[], limit = 5) {
  return combineDescriptions(
    [...transactions]
      .sort((a, b) => `${b.date}-${b.updatedAt}`.localeCompare(`${a.date}-${a.updatedAt}`))
      .map((transaction) => transaction.description.trim())
      .filter(Boolean),
    [],
    limit
  );
}

function combineDescriptions(primary: string[], secondary: string[] = [], limit = 5) {
  const seen = new Set<string>();
  const descriptions: string[] = [];

  for (const description of [...primary, ...secondary]) {
    const normalized = description.trim();
    const key = normalized.toLowerCase();
    if (!normalized || seen.has(key)) continue;
    seen.add(key);
    descriptions.push(normalized);
    if (descriptions.length === limit) break;
  }

  return descriptions;
}

function getPreferredMonth(transactions: TreasuryTransaction[], firstReportMonth = '') {
  const transactionMonths = Array.from(new Set(transactions.map((transaction) => transaction.date.slice(0, 7)))).sort().reverse();
  return transactionMonths[0] ?? (firstReportMonth || currentMonthKey());
}

export default App;
